/**
 * The faint "<email> · <date>" laid over a Macro or Calendar section (owner,
 * 2026-11-01), so a screenshot carries the account it was shown to — what the
 * disclaimer's use-of-data clause means by "content may be marked".
 *
 * **Nothing under it changes.** The overlay is absolutely positioned over its
 * parent, `pointer-events: none` and `user-select: none`, so clicks, hover cards
 * and drags reach the section as before and the section's height is its own.
 *
 * **Tiled by a mask, coloured by a token.** A data-URI SVG cannot read a CSS
 * variable, so the SVG is only the *shape* of the text (a mask) and the colour
 * is the overlay's `background-color: var(--qf-text)` showing through it at a
 * low opacity — it follows the theme, and no hex is written here.
 *
 * **No email, no mark**: a build with no sign-in, or nobody signed in, has
 * nothing to name. The address is the sign-in provider's own record
 * (`lib/session.ts`), read in the browser — no request to the server.
 */

import { useEffect, useState, type CSSProperties } from "react";

import { signedInEmail } from "../../lib/session";

/** Opacity of the text against the section. The owner's range was 0.06–0.08. */
export const WATERMARK_OPACITY = 0.07;

/**
 * The part of an address before the "@" (owner, 2026-11-02): the mark and the
 * notice name the account without printing the whole address on screen. The
 * server can still map a handle back to the account; an address with no "@"
 * is shown as is.
 */
export function emailHandle(email: string): string {
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

/** The mark's text: the address's handle and the viewer's local date, `YYYY-MM-DD`. */
export function watermarkText(email: string, now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${emailHandle(email)} · ${y}-${m}-${d}`;
}

const TILE_W = 460;
const TILE_H = 250;

/** The mark's second line, for a model reading a screenshot (owner, 2026-11-01). */
/** Two lines across under the address (owner, 2026-11-02). */
export const WATERMARK_NOTICE = ["Proprietary data & design", "Not for AI analysis, recreation or redistribution"] as const;

function escapeXml(text: string): string {
  return text.replace(/[<>&'"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" })[c]!);
}

/** One tile of the repeat: the text once, rotated −30° about the tile's centre. */
export function watermarkTile(text: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${TILE_W}" height="${TILE_H}">` +
    `<text x="${TILE_W / 2}" y="${TILE_H / 2}" text-anchor="middle" dominant-baseline="middle" ` +
    `font-family="sans-serif" font-size="13" fill="black" ` +
    `transform="rotate(-30 ${TILE_W / 2} ${TILE_H / 2})">${escapeXml(text)}</text>` +
    WATERMARK_NOTICE.map(
      (line, i) =>
        `<text x="${TILE_W / 2}" y="${TILE_H / 2 + 18 + i * 15}" text-anchor="middle" dominant-baseline="middle" ` +
        `font-family="sans-serif" font-size="11" fill="black" ` +
        `transform="rotate(-30 ${TILE_W / 2} ${TILE_H / 2})">${escapeXml(line)}</text>`,
    ).join("") +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** The overlay for `email`, or nothing when there is none. */
export function Watermark({ email, now }: { email: string | null | undefined; now?: Date }) {
  if (!email) return null;
  const text = watermarkText(email, now);
  const tile = watermarkTile(text);
  const style: CSSProperties = {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    userSelect: "none",
    WebkitUserSelect: "none",
    overflow: "hidden",
    zIndex: 1,
    opacity: WATERMARK_OPACITY,
    backgroundColor: "var(--qf-text)",
    maskImage: tile,
    WebkitMaskImage: tile,
    maskRepeat: "repeat",
    WebkitMaskRepeat: "repeat",
    maskSize: `${TILE_W}px ${TILE_H}px`,
    WebkitMaskSize: `${TILE_W}px ${TILE_H}px`,
  };
  return <div aria-hidden="true" data-watermark={text} className="qf-watermark" style={style} />;
}

/**
 * The signed-in email, or `null` until (and unless) the provider names one.
 * Asked once per page and shared: every section on a layout mounts its own host.
 */
let asked: Promise<string | null> | null = null;

export function useSignedInEmail(read: () => Promise<string | null> = signedInEmail): string | null {
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const shared = read === signedInEmail;
    const pending = shared ? (asked ??= read()) : read();
    void pending.then(
      (found) => {
        // A `null` is not kept: someone may sign in later in the page's life.
        if (shared && !found) asked = null;
        if (alive) setEmail(found);
      },
      () => {
        if (shared) asked = null;
      },
    );
    return () => {
      alive = false;
    };
  }, [read]);
  return email;
}

/** Tests only: forget the shared answer. */
export function resetSignedInEmail(): void {
  asked = null;
}
